import { useNavigate, useParams, useRoutes } from "react-router";
import { Suspense, useEffect } from "react";
import { Spinner } from "../../design-system/utils/Spinner/Spinner.jsx";
import NotFoundPage from "../not-found/NotFoundPage.jsx";
import RouteBoundary from "../../lib/RouteBoundary";
import { MODELS, useMultiModel } from "../../api";
import type { Spark } from "../../api/spark";
import { HeaderLessShell, MiniShell } from "../shell/Shell.jsx";
import FullLibraryItem from "../library/FullLibraryItem.jsx";
import LibraryOverview from "../library/LibraryOverview.jsx";
import type { DemoSparkBag } from "../workshops/sparkBag";
import SparkOverviewScreen from "../workshops/spark-overview/SparkOverview";
import SparkStepOverview from "../workshops/spark-step/SparkStepOverview";
import SparkShowroomOverview from "../workshops/showroom/SparkShowroomOverview";
import FullShowroomEntry from "../workshops/showroom/FullShowroomEntry";

// Todo: Add "getUrl" functions, use these in the toolbar etc.
// Everything that needs nothing from the shell. The routes whose screens take
// spark data as a prop are built inside the component instead, where that data
// exists.
const routes = [
  { path: "/library", element: <LibraryOverview /> },
  { path: "/library/:libraryItemId", element: <FullLibraryItem /> },
  // Unknown sub-route is an explicit, in-shell 404 — no silent redirect.
  { path: "*", element: <NotFoundPage /> },
];

/**
 * Renders the views for the demo mode, to make Lernreisen templates available to the public.
 */
function DemoShell() {
  // DemoShell is itself the route Layout and fetches the (suspending) spark, so it
  // self-hosts the outer boundary/suspense: 404/error in neutral chrome, loading
  // shows a neutral skeleton (not the suspending shell).
  return (
    <RouteBoundary Shell={HeaderLessShell}>
      <Suspense fallback={<HeaderLessShell isLoading />}>
        <DemoShellContent />
      </Suspense>
    </RouteBoundary>
  );
}

function DemoShellContent() {
  const { sparkSlug } = useParams<{ sparkSlug: Spark["slug"] }>();
  // Suspends while loading; a bad spark slug 404s to the outer RouteBoundary.
  const [spark, me] = useMultiModel([
    MODELS.SPARK({ slug: sparkSlug ?? "", withDefaultSettings: true }),
    MODELS.ME,
  ]);

  const navigate = useNavigate();

  // The same bag `SparkShell` hands down, minus the workshop the demo hasn't
  // got: the spark's own defaults stand in for a Lernreise's settings.
  const sparkBag: DemoSparkBag = {
    workshop: null,
    spark,
    structure: spark.structure,
    settings: spark.defaultSettings,
  };

  const element = useRoutes([
    {
      path: "/",
      element: <SparkOverviewScreen bag={sparkBag} />,
    },
    {
      path: "/step/:stepSlug",
      element: <SparkStepOverview bag={sparkBag} />,
    },
    {
      path: "/showroom",
      element: <SparkShowroomOverview bag={sparkBag} />,
    },
    {
      path: "/showroom/:smallTeamId",
      element: <FullShowroomEntry bag={sparkBag} />,
    },
    ...routes,
  ]);

  // A registered student has no email of their own — they belong in their
  // Lernreise rather than in the demo.
  useEffect(() => {
    if (me && (me.isAnon || !me.email)) {
      void navigate("/");
    }
  }, [navigate, me]);

  // In all other cases, render the correct component for the specified route.
  return (
    <HeaderLessShell>
      {/* Inner layer: per-step content keeps the shell; spinner while a step
          loads, in-shell 404/error for per-step data. */}
      <RouteBoundary Shell={MiniShell}>
        <Suspense fallback={<Spinner />}>{element}</Suspense>
      </RouteBoundary>
    </HeaderLessShell>
  );
}

export default DemoShell;
